const normalizeText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const parseNumericRange = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  // strip หน่วยที่ต่อท้ายตัวเลข เช่น "5-10Cells/HPF" → "5-10", "3-5/HPF" → "3-5"
  const text = String(value).trim().replace(/\s+/g, "").replace(/[a-zA-Z\/µ]+.*$/, "");
  if (!text) return null;

  const rangeMatch = text.match(/^(-?\d+(?:\.\d+)?)-(-?\d+(?:\.\d+)?)$/);
  if (rangeMatch) {
    return {
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2]),
      raw: text,
    };
  }

  const lessThanMatch = text.match(/^<(-?\d+(?:\.\d+)?)$/);
  if (lessThanMatch) {
    const max = parseFloat(lessThanMatch[1]);
    return { min: Number.NEGATIVE_INFINITY, max, raw: text };
  }

  const greaterThanMatch = text.match(/^>(-?\d+(?:\.\d+)?)$/);
  if (greaterThanMatch) {
    const min = parseFloat(greaterThanMatch[1]);
    return { min, max: Number.POSITIVE_INFINITY, raw: text };
  }

  const singleValueMatch = text.match(/^(-?\d+(?:\.\d+)?)$/);
  if (singleValueMatch) {
    const num = parseFloat(singleValueMatch[1]);
    return { min: num, max: num, raw: text };
  }

  return null;
};

export const compareValues = (normalRange, resultValue, paramName = "") => {
  if (
    !normalRange ||
    !resultValue ||
    normalRange === "xxx" ||
    resultValue === "xxx" ||
    normalRange === "-" ||
    resultValue === "-"
  ) {
    return { status: "normal", label: "-" };
  }

  if (paramName === "Stool Color") {
    return { status: "normal", label: "-" };
  }

  const normalRangeText = String(normalRange).trim();
  const resultValueText = String(resultValue).trim();
  const normalRangeIsGenderFormat = /^[MF]\s/.test(normalRangeText);

  if (/[a-zA-Z]/.test(normalRangeText) && !normalRangeIsGenderFormat) {
    const normalizedResult = normalizeText(resultValueText);
    const normalValues = normalRangeText
      .split(",")
      .map((value) => normalizeText(value));

    if (normalValues.includes(normalizedResult)) {
      return { status: "normal", label: "Normal" };
    }

    return { status: "high", label: "High" };
  }

  const normalNumericRange = parseNumericRange(normalRangeText);
  const resultNumericRange = parseNumericRange(resultValueText);

  if (normalNumericRange && resultNumericRange) {
    if (
      resultNumericRange.min >= normalNumericRange.min &&
      resultNumericRange.max <= normalNumericRange.max
    ) {
      return { status: "normal", label: "Normal" };
    }

    return { status: "high", label: "Abnormal" };
  }

  const result = parseFloat(resultValueText);
  if (Number.isNaN(result)) {
    return { status: "normal", label: "-" };
  }

  if (normalRangeText.includes("-")) {
    const [min, max] = normalRangeText
      .split("-")
      .map((value) => parseFloat(value.trim()));

    if (result < min) {
      return { status: "low", label: "Low" };
    }

    if (result > max) {
      return { status: "high", label: "High" };
    }

    return { status: "normal", label: "Normal" };
  }

  if (normalRangeText.startsWith("<")) {
    const max = parseFloat(normalRangeText.replace("<", "").trim());
    if (result >= max) {
      return { status: "high", label: "High" };
    }
    return { status: "normal", label: "Normal" };
  }

  if (normalRangeText.startsWith(">")) {
    const min = parseFloat(normalRangeText.replace(">", "").trim());
    if (result <= min) {
      return { status: "low", label: "Low" };
    }
    return { status: "normal", label: "Normal" };
  }

  return { status: "normal", label: "Normal" };
};

const STOOL_RULES = {
  stoolWbc: "Not found, NoWBCSeen, Negative",
  stoolRbc: "Not found, NoRBCSeen, Negative",
  parasite: "Not found",
  hemoglobinDetectionStool: "Negative",
};

const STOOL_LABELS = {
  stoolWbc: "WBC (Stool)",
  stoolRbc: "RBC (Stool)",
  parasite: "Parasite",
  hemoglobinDetectionStool: "Hemoglobin detection stool",
};

export const compareStoolResults = (stoolData = {}) => {
  const comparisons = {};
  const abnormalFindings = [];

  Object.entries(STOOL_RULES).forEach(([field, normalRange]) => {
    const value = stoolData[field];
    const label = STOOL_LABELS[field];
    const comparison = compareValues(normalRange, value, label);
    comparisons[field] = comparison;

    if (comparison.status !== "normal" && comparison.label !== "-") {
      abnormalFindings.push(label);
    }
  });

  return {
    status: abnormalFindings.length === 0 ? "normal" : "abnormal",
    color: abnormalFindings.length === 0 ? "green" : "red",
    abnormalFindings,
    comparisons,
  };
};

export const isStoolNormalValue = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) return true;

  return [
    "notfound",
    "nowbcseen",
    "no wbc seen",
    "norbcseen",
    "no rbc seen",
    "negative",
    "neg",
    "none",
    "brown",
  ].includes(normalized);
};

export { normalizeText, parseNumericRange };
